import React, { useEffect, useState } from 'react';
import { Hourglass } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import ConfirmDialog from '../ConfirmDialog';
import { updatePaymentExpiry } from '../../services/organisation.service';
import { clearOrganisationCache } from '../../hooks/useOrganisation';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';

const MIN_EXPIRY_HOURS = 1;
const MAX_EXPIRY_HOURS = 8760;
const DEFAULT_EXPIRY_HOURS = 72;

const UNIT_HOURS = {
    hours: 1,
    days: 24,
    weeks: 168,
};

const UNIT_LABELS = {
    hours: { singular: 'hour', plural: 'hours' },
    days: { singular: 'day', plural: 'days' },
    weeks: { singular: 'week', plural: 'weeks' },
};

const toAmountAndUnit = (hours) => {
    if (hours % UNIT_HOURS.weeks === 0) return { amount: hours / UNIT_HOURS.weeks, unit: 'weeks' };
    if (hours % UNIT_HOURS.days === 0) return { amount: hours / UNIT_HOURS.days, unit: 'days' };
    return { amount: hours, unit: 'hours' };
};

const formatDuration = (amount, unit) =>
    `${amount} ${amount === 1 ? UNIT_LABELS[unit].singular : UNIT_LABELS[unit].plural}`;

export default function PaymentExpiryCard({ paymentExpiryHours, onUpdated }) {
    const { user } = useAuth();
    const { toast } = useToast();

    const savedEnabled = paymentExpiryHours != null;
    const initial = toAmountAndUnit(savedEnabled ? paymentExpiryHours : DEFAULT_EXPIRY_HOURS);

    const [enabled, setEnabled] = useState(savedEnabled);
    const [amount, setAmount] = useState(String(initial.amount));
    const [unit, setUnit] = useState(initial.unit);

    useEffect(() => {
        const next = toAmountAndUnit(paymentExpiryHours ?? DEFAULT_EXPIRY_HOURS);
        setEnabled(paymentExpiryHours != null);
        setAmount(String(next.amount));
        setUnit(next.unit);
    }, [paymentExpiryHours]);

    const numericAmount = Number(amount);
    const isWholeAmount = amount !== '' && Number.isInteger(numericAmount) && numericAmount > 0;
    const totalHours = isWholeAmount ? numericAmount * UNIT_HOURS[unit] : null;
    const isInRange =
        totalHours !== null && totalHours >= MIN_EXPIRY_HOURS && totalHours <= MAX_EXPIRY_HOURS;
    const maxAmountForUnit = Math.floor(MAX_EXPIRY_HOURS / UNIT_HOURS[unit]);

    let validationError = null;
    if (enabled && !isWholeAmount) {
        validationError = 'Enter a whole number greater than 0.';
    } else if (enabled && !isInRange) {
        validationError = `Maximum is ${formatDuration(maxAmountForUnit, unit)} (1 year).`;
    }

    const requestValue = enabled ? totalHours : null;
    const isDirty = enabled !== savedEnabled || (enabled && totalHours !== paymentExpiryHours);
    const canSave = isDirty && !validationError;

    const handleConfirm = async () => {
        const response = await updatePaymentExpiry(user.organisationId, requestValue, user.token);

        if (response && response.status === 200) {
            clearOrganisationCache();
            toast({
                title: 'Success',
                description: enabled ? 'Payment expiry updated' : 'Payment expiry disabled',
                variant: 'success',
            });
            onUpdated && onUpdated();
        } else {
            const message = response?.data?.error?.message || 'Error updating payment expiry';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    const confirmDescription = enabled
        ? `Unauthorised payments will expire after ${formatDuration(numericAmount, unit)}. Save changes?`
        : 'Payments will no longer expire automatically. Save changes?';

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
                        <Hourglass className="h-4 w-4 text-slate-500" />
                    </span>
                    <h3 className="my-0">Payment Expiry</h3>
                </div>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                    Automatically expire payments that have not been fully authorised within a set time.
                </p>

                <label className="flex items-start gap-3 cursor-pointer">
                    <Switch
                        checked={enabled}
                        onCheckedChange={setEnabled}
                        className="mt-0.5"
                    />
                    <span className="text-sm text-gray-600 leading-snug">
                        Expire unauthorised payments
                    </span>
                </label>

                {enabled && (
                    <div className="mt-4">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 shrink-0">Expire after</span>
                            <Input
                                type="number"
                                min="1"
                                max={maxAmountForUnit}
                                step="1"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                aria-invalid={Boolean(validationError)}
                                className="h-9 flex-1 min-w-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <Select value={unit} onValueChange={setUnit}>
                                <SelectTrigger className="w-[110px] h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="hours">Hours</SelectItem>
                                    <SelectItem value="days">Days</SelectItem>
                                    <SelectItem value="weeks">Weeks</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {validationError && (
                            <p className="text-xs text-red-600 mt-2 mb-0">{validationError}</p>
                        )}
                    </div>
                )}

                {canSave ? (
                    <ConfirmDialog
                        title="Update Payment Expiry"
                        description={confirmDescription}
                        cancel="Cancel"
                        action="Update"
                        handleAction={handleConfirm}
                    >
                        <Button className="mt-6 yvr-button">
                            Save Changes
                        </Button>
                    </ConfirmDialog>
                ) : (
                    <Button disabled className="mt-6 yvr-button">
                        Save Changes
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}
